import type { AxiosError } from "axios";

import type { CreateAdminFormType } from "~/forms/CreateAdminForm/schema";
import type { UpdateAdminFormType } from "~/forms/UpdateAdminForm/schema";
import { api } from "~/utils/api";

import type { AdminUser } from "../authentication/types";
import { insertToast } from "../toasts/services";
import { personalTrainersAtom } from "./data";
import type {
	CreatePersonalTrainerDTO,
	PersonalTrainerLicenseChangeDTO,
	UpdatePersonalTrainerAdminDTO,
} from "./schemas";
import type {
	AdminQuery,
	IlistPersonalTrainerDTO,
	ListAdminsDTO,
	PersonalTrainer,
	PersonalTrainerLicense,
	PersonalTrainerQuery,
} from "./types";

export async function listPersonalTrainer(params?: PersonalTrainerQuery) {
	await api
		.get<IlistPersonalTrainerDTO>("/admin/personal-trainers", {
			params,
		})
		.then(({ data }) => {
			personalTrainersAtom.set(data);
		})
		.catch((error: AxiosError<APIError>) => {
			console.log(error.response?.data.message);
		});
}

export async function createPersonalTrainer(payload: CreatePersonalTrainerDTO) {
	return api
		.post<PersonalTrainer>("/admin/personal-trainers", payload)
		.then(({ data }) => {
			const old = personalTrainersAtom.get();
			personalTrainersAtom.set({
				...old,
				personalTrainers: [data, ...old.personalTrainers],
			});
		})
		.catch((error: AxiosError<APIError>) => {
			if (error.response?.data.message) {
				insertToast({
					duration: 3,
					message: error.response?.data.message,
					title: "Erro ao tentar criar personal trainer",
				});
			}
		});
}

export async function adminUpdatePersonalTrainer(
	personalTrainerID: string,
	payload: UpdatePersonalTrainerAdminDTO,
) {
	return api
		.patch<PersonalTrainer>(
			`/admin/personal-trainers/${personalTrainerID}`,
			payload,
		)
		.then(({ data }) => {
			const newList = personalTrainersAtom.get();
			const index = newList.personalTrainers.findIndex(
				(pt) => pt.id === data.id,
			);

			if (index >= 0) {
				newList.personalTrainers.splice(index, 1, data);
				personalTrainersAtom.set({ ...newList });
			}

			insertToast({
				duration: 3,
				message: "Dados do personal trainer atualizados.",
				title: "Sucesso!",
			});

			return true;
		})
		.catch((error: AxiosError<APIError>) => {
			if (error.response?.data.message) {
				insertToast({
					duration: 3,
					message: error.response?.data.message,
					title: "Erro ao tentar atualizar personal trainer",
					type: "error",
				});
			}
			return false;
		});
}

export async function listPersonalTrainerLicenseChanges(
	personalTrainerId: string,
	controller: AbortController,
) {
	const data = await api
		.get<PersonalTrainerLicense[]>(
			`/admin/personal-trainers/${personalTrainerId}/licenses`,
			{ signal: controller.signal },
		)
		.then(({ data }) => data)
		.catch((error: AxiosError<APIError>) => {
			if (error.response?.data.message) {
				insertToast({
					duration: 3,
					message: error.response?.data.message,
					title: "Erro ao buscar licenças",
				});
			}
		});
	return data;
}

export async function registerPersonalTrainerLicenseChange(
	personalTrainerId: string,
	payload: PersonalTrainerLicenseChangeDTO,
	controller: AbortController,
): Promise<PersonalTrainerLicense | undefined> {
	return await api
		.post(`/admin/personal-trainers/${personalTrainerId}/licenses`, payload, {
			signal: controller.signal,
		})
		.then(({ data }) => {
			const { meta, personalTrainers } = personalTrainersAtom.get();

			const newList = personalTrainers.map((trainer) =>
				trainer.id === personalTrainerId
					? {
							...trainer,
							personalTrainerLicense: [data],
						}
					: trainer,
			);

			personalTrainersAtom.set({
				meta,
				personalTrainers: newList,
			});

			return data;
		})
		.catch((error: AxiosError<APIError>) => {
			if (error.response?.data.message) {
				insertToast({
					duration: 3,
					message: error.response?.data.message,
					title: "Erro ao tentar criar licença",
				});
			}
		});
}

export async function deletePersonalTrainer(personalTrainerId: string) {
	return api
		.delete<PersonalTrainer>(`/admin/personal-trainers/${personalTrainerId}`)
		.then(({ data }) => data)
		.catch((e: AxiosError<APIError>) => {
			throw e;
		});
}

export async function listAdmins(query: AdminQuery, signal?: AbortSignal) {
	return api
		.get<ListAdminsDTO>("/admin/managers", {
			signal,
			params: {
				...query,
			},
		})
		.then(({ data }) => data)
		.catch((e: AxiosError<APIError>) => {
			if (e.response) {
				insertToast({
					duration: 4,
					message: e.response?.data.message || "Erro ao realizar ação",
					title: "Erro na requisição",
					type: "error",
				});
			}
		});
}

export async function createAdmin(payload: CreateAdminFormType) {
	return api
		.post<AdminUser>("/admin/managers", payload)
		.then(({ data }) => {
			insertToast({
				type: "success",
				duration: 4,
				message: "Administrador cadastrado",
				title: "Sucesso!",
			});
			return data;
		})
		.catch((e: AxiosError<APIError>) => {
			if (e.response) {
				insertToast({
					duration: 4,
					message: e.response?.data.message || "Erro ao realizar ação",
					title: "Erro na requisição",
					type: "error",
				});
			}
		});
}

export async function updateAdmin(payload: UpdateAdminFormType) {
	return api
		.patch<AdminUser>("/admin/update", payload)
		.then(({ data }) => {
			insertToast({
				type: "success",
				duration: 4,
				message: "Dados do perfil atualizados",
				title: "Sucesso!",
			});

			return data;
		})
		.catch((e: AxiosError<APIError>) => {
			if (e.response) {
				insertToast({
					duration: 4,
					message: e.response?.data.message || "Erro ao realizar ação",
					title: "Erro na requisição",
					type: "error",
				});
			}
		});
}

export async function updateManager(id: string, payload: UpdateAdminFormType) {
	return api
		.patch(`/admin/managers/${id}`, payload)
		.then(({ data }) => {
			insertToast({
				type: "success",
				duration: 4,
				message: "Dados do perfil atualizados",
				title: "Sucesso!",
			});

			return data;
		})
		.catch((e: AxiosError<APIError>) => {
			if (e.response) {
				insertToast({
					duration: 4,
					message: e.response?.data.message || "Erro ao realizar ação",
					title: "Erro na requisição",
					type: "error",
				});
			}
		});
}
